import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled, { ThemeProvider } from 'styled-components';
import darkTheme from 'themes/darkTheme';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import AddressForm from '../components/AddressForm';
import ProgressHeader from '../components/ProgressHeader';
import { useSignup } from '../context/SignupContext';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
  padding: 32px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CardSubtitle = styled.p`
  font-size: 15px;
  color: #94a3b8;
  margin: 0 0 32px 0;
  line-height: 1.6;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  button {
    width: auto;
    min-width: 140px;
    flex: 0 0 auto;
  }

  @media (max-width: 768px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const ShippingAddressStep = () => {
  const { formData, updateField, errors, getTotalSteps, getStepNumber } =
    useSignup();
  const history = useHistory();
  const [sameAsTrack, setSameAsTrack] = useState(false);

  const handleAddressChange = (field, value) => {
    updateField('shipping_address', {
      ...formData.shipping_address,
      [field]: value,
    });
  };

  const handleAddressSelect = (parsed) => {
    updateField('shipping_address', {
      ...formData.shipping_address,
      ...parsed,
    });
  };

  const handleSameAsToggle = (checked) => {
    setSameAsTrack(checked);
    if (checked) {
      // Copy track address to shipping address
      updateField('shipping_address', { ...formData.track_address });
    } else {
      // Clear shipping address
      updateField('shipping_address', {});
    }
  };

  const isFormValid =
    sameAsTrack ||
    (formData.shipping_address?.street1 &&
      formData.shipping_address?.city &&
      formData.shipping_address?.state &&
      formData.shipping_address?.zipcode);

  const handleContinue = () => {
    if (!sameAsTrack) {
      const address = formData.shipping_address || {};

      // Validate required fields
      if (
        !address.street1 ||
        !address.city ||
        !address.state ||
        !address.zipcode
      ) {
        toast.error('Please fill in all required address fields');
        return;
      }
    }

    // Navigate to next step
    history.push('/promoter-signup/mailing-address');
  };

  const handleBack = () => {
    // Navigate back based on promoter type
    // Track promoters come from track-address page
    // Series and Independent promoters come from business-details
    if (formData.promoter_type === 'track') {
      history.push('/promoter-signup/track-address');
    } else {
      history.push('/promoter-signup/business-details');
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <PageContainer>
        <ContentWrapper>
          <ProgressHeader
            currentStep={getStepNumber('/promoter-signup/shipping-address')}
            totalSteps={getTotalSteps()}
            stepTitle="Shipping Address"
          />

          <Card>
            <CardSubtitle>
              {formData.promoter_type === 'track'
                ? 'Where should we ship your hardware and materials?'
                : 'Where should we ship your hardware and materials? This will also serve as your primary business address.'}
            </CardSubtitle>

            <AddressForm
              address={formData.shipping_address || {}}
              onChange={handleAddressChange}
              onAddressSelect={handleAddressSelect}
              errors={errors.shipping_address || {}}
              showSameAsCheckbox={formData.promoter_type === 'track'}
              sameAsChecked={sameAsTrack}
              onSameAsToggle={handleSameAsToggle}
              idPrefix="shipping_"
            />

            <ButtonRow>
              <ButtonV2 onClick={handleBack}>← Back</ButtonV2>
              <ButtonV2
                onClick={handleContinue}
                disabled={!isFormValid}
                primary={!!isFormValid}
              >
                Continue →
              </ButtonV2>
            </ButtonRow>
          </Card>
        </ContentWrapper>
      </PageContainer>
    </ThemeProvider>
  );
};

export default ShippingAddressStep;
